import { ExclamationCircleIcon, TerminalIcon } from "@heroicons/react/outline";

import ThinkingAccordion from "../ThinkingAccordion";
import { CopyButton } from "../ui/copy-button";
import { Citations } from "../Citations";

import { formatJson, getStringContent, getThinkingContent } from "@/utils/utils";
import { Message } from "@/types";

const TRUNCATE_PREVIEW_LENGTH = 100;

const ActionCallAndResponsePreview = ({
  message,
  textContent,
}: {
  message: Message;
  textContent: string;
}) => {
  if (message.role !== "assistant") {
    return null;
  }

  const truncatedText =
    textContent.length > TRUNCATE_PREVIEW_LENGTH
      ? textContent.substring(0, TRUNCATE_PREVIEW_LENGTH) + "..."
      : textContent;

  if (message.tool_calls) {
    return (
      <>
        <div className="group-hover:inherit pt-2 text-left">{truncatedText}</div>
        <div className="group-hover:inherit pt-2 text-left font-mono">
          <TerminalIcon className="mr-1 inline h-4 w-4 text-gray-500" />
          {message.tool_calls.map((tool_call, index: number) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={index}>
              {`${tool_call.function.name} <- ${tool_call.function.arguments}`.substring(
                0,
                TRUNCATE_PREVIEW_LENGTH,
              )}
              {tool_call.function.name.length + tool_call.function.arguments.length >
              TRUNCATE_PREVIEW_LENGTH
                ? "..."
                : ""}
              <br />
            </div>
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="group-hover:inherit pt-2 text-left">{truncatedText}</div>
      <div className="group-hover:inherit pt-2 text-left font-mono">
        <TerminalIcon className="mr-1 inline h-4 w-4 text-gray-500" />
        {message.function_call
          ? `${message.function_call.name} <- ${message.function_call.arguments}`.substring(
              0,
              TRUNCATE_PREVIEW_LENGTH,
            ) +
            (message.function_call.name.length + message.function_call.arguments.length >
            TRUNCATE_PREVIEW_LENGTH
              ? "..."
              : "")
          : null}
      </div>
    </>
  );
};

const ActionCallAndResponseExpanded = ({
  message,
  textContent,
  thinkingContent,
  highlight,
}: {
  message: Message;
  textContent: string;
  thinkingContent: string | null;
  highlight: boolean;
}) => {
  if (message.role !== "assistant") {
    return null;
  }

  // eslint-disable-next-line react/no-unstable-nested-components
  const ToolCallsList = () => {
    if (message.role !== "assistant" || !message.tool_calls) {
      return null;
    }

    return (
      <>
        {message.tool_calls.map(tool_call => (
          <div key={tool_call.id || tool_call.function.name} className="font-mono">
            <div className="flex items-center font-medium text-gray-900">
              <TerminalIcon className="mr-2 inline h-4 w-4" />
              <span>{tool_call.function.name}</span>
            </div>
            <div className="pl-2 pt-1 text-gray-600">
              <div className="font-mono">
                <div className="font-medium text-gray-900">
                  <div className="flex items-start justify-between">
                    <div className="w-full overflow-x-auto">
                      {formatJson(tool_call.function.arguments)}
                    </div>
                    <div>
                      <CopyButton text={tool_call.function.arguments} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </>
    );
  };

  // eslint-disable-next-line react/no-unstable-nested-components
  const FunctionCallDisplay = () => {
    if (message.role !== "assistant") {
      return null;
    }

    return (
      <div className="font-mono">
        <div className="flex items-center font-medium text-gray-900">
          <TerminalIcon className="mr-2 inline h-4 w-4" />
          <span>{message.function_call?.name}</span>
        </div>
        <div className="pl-2 pt-1 text-gray-600">
          <div className="font-mono">
            <div className="font-medium text-gray-900">
              <div className="flex items-start justify-between">
                <div className="w-full overflow-x-auto">{message.function_call?.arguments}</div>
                <div>
                  <CopyButton text={message.function_call?.arguments ?? ""} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {thinkingContent && (
        <ThinkingAccordion
          key="thinking-content-tool-call"
          value={thinkingContent}
          id="thinking-content-tool-call"
          title="Thinking"
          className="mb-4"
        />
      )}
      <div className="flex justify-between pb-3">
        <div className="w-full">
          <span className={highlight ? "bg-blue-100 " : ""}>
            {textContent ? (
              textContent
            ) : (
              <div className="flex items-center">
                <ExclamationCircleIcon className="mr-1 h-5 w-5 text-gray-400" />
                <span className="text-gray-400">Invalid format.</span>
              </div>
            )}
          </span>
        </div>
        <div>
          <CopyButton text={JSON.stringify(textContent)} />
        </div>
      </div>
      {message.tool_calls ? <ToolCallsList /> : <FunctionCallDisplay />}
      <Citations messages={[message]} />
    </>
  );
};

export const ActionCallAndResponse = ({
  componentIsOpen,
  highlight,
  message,
}: {
  componentIsOpen: boolean;
  highlight: boolean;
  message: Message;
}) => {
  if (message.role !== "assistant") {
    return null;
  }
  const textContent = getStringContent(message);
  const thinkingContent = getThinkingContent(message);

  if (!componentIsOpen) {
    return <ActionCallAndResponsePreview message={message} textContent={textContent} />;
  }

  return (
    <ActionCallAndResponseExpanded
      message={message}
      textContent={textContent}
      thinkingContent={thinkingContent}
      highlight={highlight}
    />
  );
};
